import { Check, Copy, Info, Repeat2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import CategoryIcon from './CategoryIcon.jsx'
import UnitSelect from './UnitSelect.jsx'
import { convertValue, formatMeasurement, getUnit, parseMeasurementInput } from '../lib/conversion.js'

export default function Calculator({ locale, copy, categories, historicalNote, selectedCategoryId, onCategoryChange, onSelectionChange, requestedUnit }) {
  const category = categories.find((item) => item.id === selectedCategoryId) ?? categories[0]
  const [rawValue, setRawValue] = useState('1')
  const [fromId, setFromId] = useState(() => {
    if (requestedUnit?.categoryId !== category.id) return category.defaultFrom
    return category.units.some((unit) => unit.id === requestedUnit.unitId) ? requestedUnit.unitId : category.defaultFrom
  })
  const [toId, setToId] = useState(() => {
    if (requestedUnit?.categoryId !== category.id) return category.defaultTo
    return category.units.some((unit) => unit.id === requestedUnit.toUnitId) ? requestedUnit.toUnitId : category.defaultTo
  })
  const [includeHistorical, setIncludeHistorical] = useState(true)
  const [copied, setCopied] = useState(false)
  const historicalUnitCount = useMemo(
    () => category.units.filter((unit) => unit.ancient).length,
    [category],
  )

  const visibleUnits = useMemo(
    () => includeHistorical ? category.units : category.units.filter((unit) => !unit.ancient),
    [category, includeHistorical],
  )

  const fromUnit = getUnit(category, fromId)
  const toUnit = getUnit(category, toId)
  const inputValue = parseMeasurementInput(rawValue)
  const convertedValue = convertValue(inputValue, fromUnit, toUnit)
  const formattedResult = formatMeasurement(convertedValue)
  const historicalStatus = locale === 'bg'
    ? `${includeHistorical ? copy.showing : copy.hiding} ${historicalUnitCount} ${copy.historical} ${historicalUnitCount === 1 ? copy.unit : copy.unitsPlural} за „${category.name.toLowerCase()}“ ${copy.menuTail}`
    : locale === 'de'
      ? `${includeHistorical ? copy.showing : copy.hiding} ${historicalUnitCount} ${copy.historical} ${historicalUnitCount === 1 ? copy.unit : copy.unitsPlural} für „${category.name}“ ${copy.menuTail}`
      : `${includeHistorical ? copy.showing : copy.hiding} ${historicalUnitCount} ${copy.historical} ${category.name.toLowerCase()} ${historicalUnitCount === 1 ? copy.unit : copy.unitsPlural} ${copy.menuTail}`

  useEffect(() => {
    onSelectionChange({ category, fromUnit, toUnit })
  }, [category, fromUnit, onSelectionChange, toUnit])

  function toggleHistorical() {
    if (historicalUnitCount === 0) return
    const next = !includeHistorical
    setIncludeHistorical(next)
    if (!next) {
      if (fromUnit.ancient) setFromId(category.defaultFrom)
      if (toUnit.ancient) setToId(category.defaultTo)
    }
  }

  function swapUnits() {
    setFromId(toId)
    setToId(fromId)
    if (Number.isFinite(convertedValue)) setRawValue(String(Number(convertedValue.toPrecision(12))))
  }

  async function copyResult() {
    if (!Number.isFinite(convertedValue)) return
    await navigator.clipboard.writeText(`${formattedResult} ${toUnit.symbol}`)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1600)
  }

  return (
    <section className="calculator-shell" id="calculator" aria-label={copy.calculatorLabel}>
      <div className="category-rail" role="tablist" aria-label={copy.categoriesLabel}>
        {categories.map((item) => {
          const active = item.id === category.id
          return (
            <button
              className={active ? 'active' : ''}
              type="button"
              role="tab"
              aria-selected={active}
              key={item.id}
              onClick={() => onCategoryChange(item.id)}
            >
              <span className="category-icon"><CategoryIcon categoryId={item.id} /></span>
              <span>{item.name}</span>
              <small>{item.units.length} {copy.units}</small>
            </button>
          )
        })}
      </div>

      <div className="calculator-body">
        <div className="source-row">
          <label className="field-label value-field" htmlFor="measure-value">
            <span>{copy.value}</span>
            <input
              id="measure-value"
              inputMode="decimal"
              autoComplete="off"
              value={rawValue}
              onChange={(event) => setRawValue(event.target.value)}
              aria-invalid={rawValue !== '' && Number.isNaN(inputValue)}
            />
          </label>
          <UnitSelect locale={locale} id="from-unit" label={copy.from} units={visibleUnits} value={fromId} onChange={setFromId} />
        </div>

        <div className="target-row">
          <button className="swap-button" type="button" onClick={swapUnits} aria-label={copy.swap}>
            <Repeat2 aria-hidden="true" size={21} />
          </button>
          <UnitSelect locale={locale} id="to-unit" label={copy.to} units={visibleUnits} value={toId} onChange={setToId} />
        </div>

        <div className="result-row" aria-live="polite">
          <div className="result-copy">
            <span>{copy.result}</span>
            <strong>{formattedResult}</strong>
            <small>{toUnit.symbol}</small>
          </div>
          <button className={`copy-button${copied ? ' copied' : ''}`} type="button" onClick={copyResult} disabled={!Number.isFinite(convertedValue)}>
            {copied ? <Check aria-hidden="true" size={18} /> : <Copy aria-hidden="true" size={18} />}
            {copied ? copy.copied : copy.copyResult}
          </button>
        </div>
      </div>

      <div className="unit-context" aria-label={copy.unitContextTitle}>
        <div className="unit-context-heading">
          <Info aria-hidden="true" size={18} />
          <div>
            <strong>{copy.unitContextTitle}</strong>
            <p>{category.description}</p>
          </div>
        </div>
        <div className="unit-context-grid">
          {[[copy.from, fromUnit], [copy.to, toUnit]].map(([label, unit]) => (
            <article key={`${label}-${unit.id}`}>
              <span>{label}</span>
              <strong>{unit.name} <small>({unit.symbol})</small></strong>
              <p>{unit.description}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="historical-toggle">
        <button
          className={`switch${includeHistorical ? ' on' : ''}`}
          type="button"
          role="switch"
          aria-checked={includeHistorical}
          aria-label={copy.historicalToggle}
          disabled={historicalUnitCount === 0}
          onClick={toggleHistorical}
        ><span /></button>
        <div>
          <strong>{copy.historicalToggle} <span className="history-label">his</span></strong>
          {historicalUnitCount > 0 ? (
            <p>
              {historicalStatus} {historicalNote}
            </p>
          ) : <p>{copy.noHistorical}</p>}
        </div>
        <a href="#historical"><Info aria-hidden="true" size={16} /> {copy.learnMore}</a>
      </div>
    </section>
  )
}
